<template>
  <aside v-if="visible && content" class="plate-column-hover-card" :style="tooltipStyle" @mouseenter="$emit('mouseenter')"
    @mouseleave="$emit('mouseleave', $event)">
    <button type="button" class="tooltip-close-button" :aria-label="labels.close" :title="labels.close"
      @click.stop="$emit('close', $event)">
      <i class="fas fa-times"></i>
    </button>

    <header class="tooltip-header">
      <p class="tooltip-group">{{ content.columnEyebrow }}</p>
      <h4 class="tooltip-title">{{ content.columnTitle }}</h4>
      <p v-if="content.columnSubtitle" class="tooltip-subtitle">{{ content.columnSubtitle }}</p>
    </header>

    <section v-if="content.details && content.details.length" class="tooltip-section">
      <h5 class="tooltip-section-title">{{ labels.columnInfo }}</h5>
      <ul class="tooltip-grid details-grid">
        <li v-for="(detail, index) in content.details" :key="`detail-${index}`" class="tooltip-item">
          <span class="tooltip-label">{{ detail.label }}</span>
          <span class="tooltip-value">{{ detail.value }}</span>
        </li>
      </ul>
    </section>

    <section v-if="content.flags && content.flags.length" class="tooltip-section">
      <h5 class="tooltip-section-title">{{ labels.rulesBehavior }}</h5>
      <ul class="tooltip-grid details-grid">
        <li v-for="(flag, index) in content.flags" :key="`flag-${index}`" class="tooltip-item">
          <span class="tooltip-label">{{ flag.label }}</span>
          <span :class="['boolean-badge', flag.valueBoolean ? 'yes' : 'no']">{{ flag.value }}</span>
        </li>
      </ul>
    </section>

    <section v-if="content.groups && content.groups.length" class="tooltip-section">
      <header class="tooltip-section-header">
        <h5 class="tooltip-section-title">{{ labels.associatedGroups }}</h5>
        <p v-if="content.totalGroupsCount !== null && content.totalGroupsCount !== undefined" class="total-badge">
          {{ labels.total(content.totalGroupsCount) }}
        </p>
      </header>
      <ul class="group-count-list">
        <li v-for="(group, index) in content.groups" :key="`group-${index}`" class="group-count-card">
          <span class="group-count-name">{{ group.name }}</span>
          <span class="group-count-value">{{ group.count }}</span>
        </li>
      </ul>
    </section>
  </aside>
</template>

<script>
import { t } from 'i18next';

export default {
  name: 'PlateColumnHoverCard',
  emits: ['mouseenter', 'mouseleave', 'close'],
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    position: {
      type: Object,
      default: () => ({ top: 0, left: 0 })
    },
    content: {
      type: Object,
      default: null
    }
  },
  computed: {
    labels() {
      return {
        close: t('Fechar'),
        columnInfo: t('plate_column_hover_info_title'),
        rulesBehavior: t('plate_column_hover_rules_title'),
        associatedGroups: t('plate_column_hover_groups_title'),
        total: (value) => t('plate_column_hover_total_label', { PARAM: value })
      };
    },
    tooltipStyle() {
      return {
        top: `${this.position?.top || 0}px`,
        left: `${this.position?.left || 0}px`,
        maxHeight: `${this.position?.maxHeight || 0}px`,
        width: this.position?.width ? `${this.position.width}px` : null,
        maxWidth: this.position?.maxWidth ? `${this.position.maxWidth}px` : null
      };
    }
  }
};
</script>

<style scoped lang="less">
.plate-column-hover-card {
  position: fixed;
  z-index: 9999;
  width: min(360px, calc(100vw - 24px));
  max-height: calc(100vh - 32px);
  overflow: auto;
  overscroll-behavior: contain;
  padding: 10px;
  border-radius: 12px;
  border: 1px solid rgba(27, 41, 87, 0.18);
  background: linear-gradient(180deg, rgba(247, 249, 255, 0.98) 0%, rgba(255, 255, 255, 0.98) 100%);
  box-shadow: 0 24px 48px rgba(16, 24, 40, 0.18), 0 8px 16px rgba(16, 24, 40, 0.12);
  backdrop-filter: blur(10px);
  pointer-events: auto;
  color: #16213e;
  scrollbar-width: thin;
  scrollbar-color: rgba(53, 76, 132, 0.55) rgba(203, 212, 233, 0.6);
}

.tooltip-close-button {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 22px;
  height: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  border-radius: 999px;
  background: rgba(223, 231, 250, 0.92);
  color: #415a92;
  cursor: pointer;
  transition: background 0.18s ease, color 0.18s ease;
}

.tooltip-close-button:hover {
  background: rgba(198, 210, 240, 0.96);
  color: #1d2850;
}

.tooltip-close-button i {
  font-size: 0.7rem;
}

.plate-column-hover-card::-webkit-scrollbar {
  width: 8px;
}

.plate-column-hover-card::-webkit-scrollbar-track {
  background: rgba(203, 212, 233, 0.45);
  border-radius: 999px;
}

.plate-column-hover-card::-webkit-scrollbar-thumb {
  background: rgba(53, 76, 132, 0.55);
  border-radius: 999px;
}

.tooltip-header {
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin-bottom: 8px;
  padding-right: 24px;
}

.tooltip-group {
  margin: 0;
  font-size: clamp(0.56rem, 0.54rem + 0.14vw, 0.63rem);
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #5b6b96;
}

.tooltip-title {
  margin: 0;
  font-size: clamp(0.72rem, 0.68rem + 0.23vw, 0.81rem);
  font-weight: 800;
  line-height: 1.25;
  color: #1d2850;
}

.tooltip-subtitle {
  margin: 0;
  font-size: clamp(0.6rem, 0.57rem + 0.15vw, 0.68rem);
  font-weight: 700;
  color: #425b92;
}

.tooltip-section {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid rgba(27, 41, 87, 0.1);
}

.tooltip-section-title {
  margin: 0;
  font-size: clamp(0.56rem, 0.54rem + 0.14vw, 0.63rem);
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #5b6b96;
}

.tooltip-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
}

.tooltip-grid {
  display: grid;
  gap: 5px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.details-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.tooltip-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  padding: 6px 7px;
  border-radius: 8px;
  background: rgba(227, 234, 249, 0.45);
}

.tooltip-label {
  font-size: clamp(0.52rem, 0.5rem + 0.11vw, 0.59rem);
  font-weight: 800;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: #6676a3;
}

.tooltip-value {
  font-size: clamp(0.63rem, 0.6rem + 0.16vw, 0.7rem);
  font-weight: 700;
  line-height: 1.35;
  color: #172348;
  word-break: break-word;
}

.boolean-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
  min-height: 20px;
  padding: 2px 7px;
  border-radius: 999px;
  font-size: clamp(0.56rem, 0.53rem + 0.11vw, 0.63rem);
  font-weight: 700;
  line-height: 1.3;
}

.boolean-badge.yes {
  background: rgba(223, 244, 228, 0.95);
  color: #237c45;
  border: 1px solid rgba(35, 124, 69, 0.18);
}

.boolean-badge.no {
  background: rgba(246, 231, 231, 0.95);
  color: #9c3131;
  border: 1px solid rgba(156, 49, 49, 0.16);
}

.group-count-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 6px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.group-count-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 7px;
  border-radius: 8px;
  background: rgba(242, 245, 252, 0.95);
}

.group-count-name {
  min-width: 0;
  font-size: clamp(0.61rem, 0.58rem + 0.14vw, 0.68rem);
  font-weight: 700;
  color: #1d2850;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.group-count-value {
  flex: none;
  min-width: 22px;
  height: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 6px;
  border-radius: 999px;
  background: rgba(213, 223, 247, 0.95);
  font-size: clamp(0.56rem, 0.53rem + 0.11vw, 0.63rem);
  font-weight: 800;
  color: #28457e;
}

.total-badge {
  margin: 0;
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  border-radius: 999px;
  background: rgba(223, 231, 250, 0.92);
  font-size: clamp(0.56rem, 0.53rem + 0.11vw, 0.63rem);
  font-weight: 700;
  color: #28457e;
}

@media (max-width: 900px), (max-height: 820px) {
  .plate-column-hover-card {
    width: min(330px, calc(100vw - 18px));
    padding: 9px;
  }
}

@media (max-width: 720px) {
  .details-grid,
  .group-count-list {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
